/**
 * 2D canvas with pan/zoom and course coordinate transform. Draws course from model.
 */

import type { Course, Point, SelectionHit, ToolId } from './types';
import { SURFACE_KEYS } from './types';
import { polygonOffsetOutward } from './clipperOffset';
import {
  getLayerForSurfaceKey,
  getLayerForFeature,
  type LayerVisibility,
} from './layers';
import {
  SURFACE_COLORS,
  TEE_BOX_FILL,
  TEE_BOX_STROKE,
  TREE_COLOR,
  HOUSE_COLOR,
  getPinColor,
  getSurfaceColor,
  getTeePositionColor,
} from './colors';
import { PAR_DIMENSIONS, getHolePar } from './courseState';

const YARD_MM = 914.4;
const FRINGE_OFFSET_MM = 914.4; // 1 yard around green
const GRID_YARD = 25;
const GRID_STEP_MM = GRID_YARD * YARD_MM;
const MEASURE_ANCHOR_RADIUS_MM = 1500;
const CONTOUR_STEP_NORMALIZED = 0.05; // contour every ~6 inches (0.05 in 0–1 grid)

/** Marching squares: one cell, contour level L. Return 0–2 segments (each [x,y, x,y] in mm). */
function contourSegmentsInCell(
  level: number,
  v00: number,
  v10: number,
  v11: number,
  v01: number,
  x0: number,
  y0: number,
  cellSizeMm: number
): [number, number, number, number][] {
  const segs: [number, number, number, number][] = [];
  const t = (a: number, b: number) => (b === a ? 0.5 : (level - a) / (b - a));
  const left = x0;
  const right = x0 + cellSizeMm;
  const bottom = y0;
  const top = y0 + cellSizeMm;
  const e0 = v00 >= level ? 1 : 0;
  const e1 = v10 >= level ? 1 : 0;
  const e2 = v11 >= level ? 1 : 0;
  const e3 = v01 >= level ? 1 : 0;
  const code = e0 | (e1 << 1) | (e2 << 2) | (e3 << 3);
  switch (code) {
    case 0:
    case 15:
      break;
    case 1:
      segs.push([left, bottom + t(v00, v01) * cellSizeMm, left + t(v00, v10) * cellSizeMm, bottom]);
      break;
    case 2:
      segs.push([left + t(v00, v10) * cellSizeMm, bottom, right, bottom + t(v10, v11) * cellSizeMm]);
      break;
    case 3:
      segs.push([left, bottom + t(v00, v01) * cellSizeMm, right, bottom + t(v10, v11) * cellSizeMm]);
      break;
    case 4:
      segs.push([right, bottom + t(v10, v11) * cellSizeMm, left + t(v11, v01) * cellSizeMm, top]);
      break;
    case 5: {
      segs.push([left, bottom + t(v00, v01) * cellSizeMm, left + t(v00, v10) * cellSizeMm, bottom]);
      segs.push([right, bottom + t(v10, v11) * cellSizeMm, right, top - t(v11, v01) * cellSizeMm]);
      break;
    }
    case 6:
      segs.push([left + t(v00, v10) * cellSizeMm, bottom, left + t(v01, v11) * cellSizeMm, top]);
      break;
    case 7:
      segs.push([left, bottom + t(v00, v01) * cellSizeMm, left + t(v11, v01) * cellSizeMm, top]);
      break;
    case 8:
      segs.push([left, bottom + t(v00, v01) * cellSizeMm, left + t(v01, v11) * cellSizeMm, top]);
      break;
    case 9:
      segs.push([left + t(v00, v10) * cellSizeMm, bottom, left, bottom + t(v00, v01) * cellSizeMm]);
      break;
    case 10: {
      segs.push([left + t(v00, v10) * cellSizeMm, bottom, right, bottom + t(v10, v11) * cellSizeMm]);
      segs.push([right, bottom + t(v10, v11) * cellSizeMm, left + t(v01, v11) * cellSizeMm, top]);
      break;
    }
    case 11:
      segs.push([left + t(v00, v10) * cellSizeMm, bottom, right, bottom + t(v10, v11) * cellSizeMm]);
      break;
    case 12:
      segs.push([right, bottom + t(v10, v11) * cellSizeMm, left + t(v11, v01) * cellSizeMm, top]);
      break;
    case 13:
      segs.push([right, bottom + t(v10, v11) * cellSizeMm, left + t(v11, v01) * cellSizeMm, top]);
      break;
    case 14:
      segs.push([left, bottom + t(v00, v01) * cellSizeMm, left + t(v01, v11) * cellSizeMm, top]);
      break;
  }
  return segs;
}

export interface ViewState {
  panX: number;
  panY: number;
  zoom: number;
}

export interface DrawPreview {
  strokePoints?: Point[];
  /** Current cursor position (e.g. constrained); draws segment from last stroke point to here */
  strokePreviewEnd?: Point;
  /** Active tool; used to draw vertex handles when reshape */
  activeTool?: ToolId;
  /** Measure tool state */
  measureAnchor1?: Point | null;
  measureAnchor2?: Point | null;
  measureSegment2End?: Point | null;
  measurePhase?: 0 | 1 | 2 | 3;
  measureLivePoint?: Point | null;
  /** Selected item (Pointer tool); draw dashed white outline. */
  selectedHit?: SelectionHit | null;
  /** Hovered item (Pointer tool); draw light gray dashed outline. */
  hoverHit?: SelectionHit | null;
}

const DEFAULT_VIEW: ViewState = {
  panX: 0,
  panY: 0,
  zoom: 1,
};

export function createCanvasView(
  canvas: HTMLCanvasElement,
  getCourse: () => Course,
  getCurrentHole: () => number,
  getLayerVisibility?: () => LayerVisibility,
  getShowGrid?: () => boolean
) {
  const layerVisible = (layerId: string | null) =>
    !getLayerVisibility || layerId === null || getLayerVisibility()[layerId as keyof LayerVisibility] !== false;
  let view: ViewState = { ...DEFAULT_VIEW };
  let isPanning = false;
  let lastClientX = 0;
  let lastClientY = 0;

  /** Grid dimensions for the current hole (from par). */
  const widthMm = () => {
    const course = getCourse();
    const par = getHolePar(course, getCurrentHole());
    return PAR_DIMENSIONS[par].widthYd * YARD_MM;
  };
  const heightMm = () => {
    const course = getCourse();
    const par = getHolePar(course, getCurrentHole());
    return PAR_DIMENSIONS[par].heightYd * YARD_MM;
  };

  /** Screen to course (mm). Origin at bottom center; x right, y up. Grid center (0, hMm/2) aligns with view center. */
  function screenToMm(sx: number, sy: number): Point {
    const rect = canvas.getBoundingClientRect();
    const wMm = widthMm();
    const hMm = heightMm();
    const scale = Math.min(
      (rect.width - 40) / wMm,
      (rect.height - 40) / hMm
    ) * view.zoom;
    const originX = rect.width / 2 + view.panX;
    const originY = rect.height / 2 + view.panY;
    const dx = (sx - rect.left - originX) / scale;
    const dy = (sy - rect.top - originY) / scale;
    const mx = dx;
    const my = hMm / 2 - dy;
    return [mx, my];
  }

  /** Course (mm) to screen. Course: origin bottom center, y up. Grid center aligns with view center. */
  function mmToScreen(mx: number, my: number): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const wMm = widthMm();
    const hMm = heightMm();
    const scale = Math.min(
      (rect.width - 40) / wMm,
      (rect.height - 40) / hMm
    ) * view.zoom;
    const originX = rect.width / 2 + view.panX;
    const originY = rect.height / 2 + view.panY;
    return {
      x: originX + mx * scale,
      y: originY - (my - hMm / 2) * scale,
    };
  }

  function getScale(): number {
    const rect = canvas.getBoundingClientRect();
    return (
      Math.min(
        (rect.width - 40) / widthMm(),
        (rect.height - 40) / heightMm()
      ) * view.zoom
    );
  }

  function draw(preview?: DrawPreview) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const dpr = window.devicePixelRatio ?? 1;
    if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    }

    const w = rect.width;
    const h = rect.height;
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, w, h);

    const scale = Math.min((w - 40) / widthMm(), (h - 40) / heightMm()) * view.zoom;
    const originX = w / 2 + view.panX;
    const originY = h / 2 + view.panY;

    const wMm = widthMm();
    const hMm = heightMm();

    ctx.save();
    ctx.translate(originX, originY);
    ctx.scale(scale, scale);
    ctx.translate(0, hMm / 2);
    ctx.scale(1, -1);

    // Clip to grid bounds (par dimensions for current hole)
    ctx.beginPath();
    ctx.rect(-wMm / 2, 0, wMm, hMm);
    ctx.clip();

    // Course bounds (no grid here; grid drawn on top at end)
    ctx.strokeStyle = '#555';
    ctx.lineWidth = 2 / scale;
    ctx.strokeRect(-wMm / 2, 0, wMm, hMm);

    // Surfaces (only current hole). Fringe = 1 yd around each green.
    const course = getCourse();
    const currentHole = getCurrentHole();
    const fringeColor = SURFACE_COLORS.fringe;
    for (const key of SURFACE_KEYS) {
      if (key === 'fringe') continue;
      const layerId = getLayerForSurfaceKey(key);
      if (!layerVisible(layerId)) continue;
      const color = getSurfaceColor(key);
      const shapes = course.surfaces[key];
      if (!shapes) continue;
      for (const shape of shapes) {
        if (shape.holeNumber != null && shape.holeNumber !== currentHole) continue;
        if (shape.points.length < 2) continue;
        const pt0 = shape.points[0];
        if (!pt0) continue;
        if (key === 'green' && shape.type === 'polygon' && shape.points.length >= 3) {
          const fringeRing = polygonOffsetOutward(shape.points, FRINGE_OFFSET_MM);
          if (fringeRing.length >= 3) {
            const f0 = fringeRing[0];
            if (f0) {
              ctx.fillStyle = fringeColor;
              ctx.strokeStyle = fringeColor;
              ctx.lineWidth = 1 / scale;
              ctx.beginPath();
              ctx.moveTo(f0[0], f0[1]);
              for (let i = 1; i < fringeRing.length; i++) {
                const pt = fringeRing[i];
                if (pt) ctx.lineTo(pt[0], pt[1]);
              }
              ctx.closePath();
              ctx.fill();
              ctx.stroke();
            }
          }
        }
        ctx.strokeStyle = color;
        ctx.lineWidth = shape.type === 'path' ? 2 / scale : 1 / scale;
        ctx.beginPath();
        ctx.moveTo(pt0[0], pt0[1]);
        for (let i = 1; i < shape.points.length; i++) {
          const pt = shape.points[i];
          if (pt) ctx.lineTo(pt[0], pt[1]);
        }
        if (shape.type === 'path') {
          ctx.stroke();
        } else {
          ctx.closePath();
          ctx.fillStyle = color;
          ctx.fill();
          ctx.stroke();
        }
      }
    }

    // Tees (only current hole)
    if (layerVisible(getLayerForFeature('tees'))) {
    ctx.fillStyle = TEE_BOX_FILL;
    ctx.strokeStyle = TEE_BOX_STROKE;
    for (const tee of course.tees) {
      if (tee.holeNumber !== currentHole) continue;
      if (tee.points.length < 2) continue;
      const t0 = tee.points[0];
      if (!t0) continue;
      ctx.beginPath();
      ctx.moveTo(t0[0], t0[1]);
      for (let i = 1; i < tee.points.length; i++) {
        const t = tee.points[i];
        if (t) ctx.lineTo(t[0], t[1]);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    }

    // Trees (only current hole) — Hazards layer
    if (layerVisible(getLayerForFeature('trees'))) {
      ctx.fillStyle = TREE_COLOR;
      for (const tree of course.trees) {
        if ((tree.holeNumber ?? 1) !== currentHole) continue;
        ctx.beginPath();
        ctx.arc(tree.x, tree.y, tree.radiusMm, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Houses (only current hole) — Hazards layer, rectangular base + triangular roof (full width and length)
    if (layerVisible(getLayerForFeature('houses'))) {
      ctx.fillStyle = HOUSE_COLOR;
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1 / scale;
      for (const house of course.houses) {
        if ((house.holeNumber ?? 1) !== currentHole) continue;
        const w = house.radiusMm * 1.8;
        const d = house.radiusMm * 1.2;
        const hw = w / 2;
        const hd = d / 2;
        const roofH = house.radiusMm * 0.6;
        ctx.save();
        ctx.translate(house.x, house.y);
        const overlap = Math.max(2 / scale, 10);
        ctx.fillStyle = HOUSE_COLOR;
        ctx.fillRect(-hw, -hd, w, d);
        ctx.fillStyle = '#5a5a5a';
        ctx.beginPath();
        ctx.moveTo(-hw, hd);
        ctx.lineTo(0, hd + roofH);
        ctx.lineTo(hw, hd);
        ctx.lineTo(hw, hd - overlap);
        ctx.lineTo(-hw, hd - overlap);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-hw, -hd);
        ctx.lineTo(0, -hd - roofH);
        ctx.lineTo(hw, -hd);
        ctx.lineTo(hw, -hd + overlap);
        ctx.lineTo(-hw, -hd + overlap);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-hw, -hd);
        ctx.lineTo(0, -hd - roofH);
        ctx.lineTo(hw, -hd);
        ctx.lineTo(hw, hd);
        ctx.lineTo(0, hd + roofH);
        ctx.lineTo(-hw, hd);
        ctx.closePath();
        ctx.stroke();
        ctx.restore();
      }
    }

    const markerRadiusMm = 1500;
    const pinBaseRadiusMm = 400;
    const pinFlagWidthMm = 2200;
    const pinFlagHeightMm = 1400;
    const pole150HalfWidthMm = 500;
    const pole150HeightMm = 6000;
    const stripe150Count = 10;

    // Pins (only current hole) — top-down: small base circle + red flag triangle + day label
    if (layerVisible(getLayerForFeature('pins'))) {
    const pinDayLabel: Record<string, string> = { thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
    for (const pin of course.pins) {
      if (pin.holeNumber !== currentHole) continue;
      const px = pin.x;
      const py = pin.y;
      ctx.strokeStyle = '#555';
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 2 / scale;
      ctx.beginPath();
      ctx.arc(px, py, pinBaseRadiusMm, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      const pinColor = getPinColor(pin.day);
      ctx.fillStyle = pinColor;
      ctx.strokeStyle = pin.day === 'thu' ? '#8b0000' : pin.day === 'fri' ? '#ccc' : pin.day === 'sat' ? '#1e40af' : '#b45309';
      ctx.lineWidth = 1 / scale;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + pinFlagWidthMm, py - pinFlagHeightMm / 2);
      ctx.lineTo(px, py - pinFlagHeightMm);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      const dayText = pinDayLabel[pin.day] ?? pin.day;
      ctx.fillStyle = '#333';
      ctx.font = `${Math.max(10, 800 / scale)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.save();
      ctx.translate(px, py - pinBaseRadiusMm - 600);
      ctx.scale(1, -1);
      ctx.fillText(dayText, 0, 0);
      ctx.restore();
    }
    }

    // Tee positions (only current hole) — circle in tee box
    if (layerVisible(getLayerForFeature('teePositions'))) {
    for (const pos of course.teePositions) {
      if (pos.holeNumber !== currentHole) continue;
      ctx.fillStyle = getTeePositionColor(pos.color);
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2 / scale;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, markerRadiusMm, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    }

    // 150 yd markers (only current hole) — black-and-white barber pole
    if (layerVisible(getLayerForFeature('markers150'))) {
    for (const m of course.markers150) {
      if (m.holeNumber !== currentHole) continue;
      const mx = m.x;
      const my = m.y;
      const stripeH = pole150HeightMm / stripe150Count;
      for (let i = 0; i < stripe150Count; i++) {
        ctx.fillStyle = i % 2 === 0 ? '#ffffff' : '#1a1a1a';
        ctx.fillRect(
          mx - pole150HalfWidthMm,
          my + i * stripeH,
          pole150HalfWidthMm * 2,
          stripeH
        );
      }
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2 / scale;
      ctx.strokeRect(mx - pole150HalfWidthMm, my, pole150HalfWidthMm * 2, pole150HeightMm);
      ctx.fillStyle = '#333';
      ctx.font = `${Math.min(1400, pole150HalfWidthMm * 2 / scale)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.save();
      ctx.translate(mx, my + pole150HeightMm / 2);
      ctx.scale(1, -1);
      ctx.fillText('150', 0, 0);
      ctx.restore();
    }
    }

    // Hover outline: light gray dashed border (Pointer tool, when not selected)
    const hover = preview?.hoverHit;
    const sel = preview?.selectedHit;
    const isSameHit = (a: SelectionHit, b: SelectionHit) =>
      a.type === b.type &&
      a.index === b.index &&
      (a.type !== 'surface' || (b.type === 'surface' && a.key === b.key));
    if (hover && (!sel || !isSameHit(hover, sel))) {
      drawHitOutline(ctx, scale, course, hover, 'rgba(180,180,180,0.85)');
    }

    // Selection outline: dashed white border around selected item (Pointer tool) or item being moved
    if (sel) {
      drawHitOutline(ctx, scale, course, sel, 'rgba(255,255,255,0.95)');
    }

    function drawHitOutline(
      ctx: CanvasRenderingContext2D,
      scale: number,
      c: Course,
      h: SelectionHit,
      strokeColor: string
    ) {
      ctx.save();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 2 / scale;
      ctx.setLineDash([8 / scale, 6 / scale]);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (h.type === 'surface') {
        const shapes = c.surfaces[h.key];
        const shape = shapes?.[h.index];
        if (shape?.points && shape.points.length >= 2) {
          const pt0 = shape.points[0];
          if (pt0) {
            ctx.beginPath();
            ctx.moveTo(pt0[0], pt0[1]);
            for (let i = 1; i < shape.points.length; i++) {
              const pt = shape.points[i];
              if (pt) ctx.lineTo(pt[0], pt[1]);
            }
            if (shape.type === 'polygon') ctx.closePath();
            ctx.stroke();
          }
        }
      } else if (h.type === 'tee') {
        const tee = c.tees[h.index];
        if (tee?.points && tee.points.length >= 2) {
          const t0 = tee.points[0];
          if (t0) {
            ctx.beginPath();
            ctx.moveTo(t0[0], t0[1]);
            for (let i = 1; i < tee.points.length; i++) {
              const t = tee.points[i];
              if (t) ctx.lineTo(t[0], t[1]);
            }
            ctx.closePath();
            ctx.stroke();
          }
        }
      } else if (h.type === 'tree') {
        const tree = c.trees[h.index];
        if (tree) {
          ctx.beginPath();
          ctx.arc(tree.x, tree.y, tree.radiusMm, 0, Math.PI * 2);
          ctx.stroke();
        }
      } else if (h.type === 'house') {
        const house = c.houses[h.index];
        if (house) {
          const w = house.radiusMm * 1.8;
          const d = house.radiusMm * 1.2;
          const hw = w / 2;
          const hd = d / 2;
          ctx.strokeRect(house.x - hw, house.y - hd, w, d);
        }
      } else if (h.type === 'pin') {
        const pin = c.pins[h.index];
        if (pin) {
          ctx.beginPath();
          ctx.arc(pin.x, pin.y, pinBaseRadiusMm, 0, Math.PI * 2);
          ctx.stroke();
        }
      } else if (h.type === 'teePosition') {
        const pos = c.teePositions[h.index];
        if (pos) {
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, markerRadiusMm, 0, Math.PI * 2);
          ctx.stroke();
        }
      } else if (h.type === 'marker150') {
        const m = c.markers150[h.index];
        if (m) {
          ctx.strokeRect(
            m.x - pole150HalfWidthMm,
            m.y,
            pole150HalfWidthMm * 2,
            pole150HeightMm
          );
        }
      }
      ctx.restore();
    }

    // Reshape tool: vertex handles (small circles at each vertex of surfaces and tees)
    if (preview?.activeTool === 'reshape') {
      const handleRadiusMm = 800;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.strokeStyle = '#4a9eff';
      ctx.lineWidth = 1.5 / scale;
      for (const key of SURFACE_KEYS) {
        if (key === 'fringe') continue;
        const layerId = getLayerForSurfaceKey(key);
        if (!layerVisible(layerId)) continue;
        const shapes = course.surfaces[key];
        if (!shapes) continue;
        for (const shape of shapes) {
          if (shape.holeNumber != null && shape.holeNumber !== currentHole) continue;
          if (shape.points.length < 2 || key === 'secondCut') continue;
          for (const pt of shape.points) {
            if (pt) {
              ctx.beginPath();
              ctx.arc(pt[0], pt[1], handleRadiusMm, 0, Math.PI * 2);
              ctx.fill();
              ctx.stroke();
            }
          }
        }
      }
      if (layerVisible(getLayerForFeature('tees'))) {
        for (const tee of course.tees) {
          if (tee.holeNumber !== currentHole || !tee.points.length) continue;
          for (const pt of tee.points) {
            if (pt) {
              ctx.beginPath();
              ctx.arc(pt[0], pt[1], handleRadiusMm, 0, Math.PI * 2);
              ctx.fill();
              ctx.stroke();
            }
          }
        }
      }
    }

    // Preview: current stroke — white line, isolated so no prior stroke/fill leaks.
    const strokePts = preview?.strokePoints;
    const strokeEnd = preview?.strokePreviewEnd;
    if (strokePts && strokePts.length > 0) {
      const pts = strokeEnd ? [...strokePts, strokeEnd] : strokePts;
      if (pts.length >= 2) {
        const p0 = pts[0];
        if (p0) {
          ctx.save();
          ctx.beginPath();
          ctx.strokeStyle = 'rgba(255,255,255,0.9)';
          ctx.lineWidth = 2 / scale;
          ctx.moveTo(p0[0], p0[1]);
          for (let i = 1; i < pts.length; i++) {
            const p = pts[i];
            if (p) ctx.lineTo(p[0], p[1]);
          }
          ctx.stroke();
          ctx.restore();
        }
      }
    }

    // Grid on top: 25 yd increments, light gray (optional)
    if (getShowGrid === undefined || getShowGrid()) {
      ctx.strokeStyle = '#b0b0b0';
      ctx.lineWidth = 1 / scale;
      for (let x = -wMm / 2; x <= wMm / 2 + 1; x += GRID_STEP_MM) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, hMm);
        ctx.stroke();
      }
      for (let y = 0; y <= hMm + 1; y += GRID_STEP_MM) {
        ctx.beginPath();
        ctx.moveTo(-wMm / 2, y);
        ctx.lineTo(wMm / 2, y);
        ctx.stroke();
      }
    }

    // Elevation contours (isolines) when grid is shown — standard 2D topo visualization
    const grid = course.elevation?.grid;
    if ((getShowGrid === undefined || getShowGrid()) && grid?.values?.length) {
      const { cols, rows, cellSizeMm } = grid;
      const halfW = wMm / 2;
      const values = grid.values;
      const getVal = (c: number, r: number) =>
        values[Math.min(Math.max(r, 0), rows - 1) * cols + Math.min(Math.max(c, 0), cols - 1)] ?? 0;
      ctx.strokeStyle = 'rgba(180, 140, 100, 0.85)';
      ctx.lineWidth = 1.2 / scale;
      for (let L = CONTOUR_STEP_NORMALIZED; L < 1; L += CONTOUR_STEP_NORMALIZED) {
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const x0 = c * cellSizeMm - halfW;
            const y0 = hMm - (r + 1) * cellSizeMm;
            const v00 = getVal(c, r);
            const v10 = getVal(c + 1, r);
            const v11 = getVal(c + 1, r + 1);
            const v01 = getVal(c, r + 1);
            const segs = contourSegmentsInCell(L, v00, v10, v11, v01, x0, y0, cellSizeMm);
            for (const [x1, y1, x2, y2] of segs) {
              ctx.beginPath();
              ctx.moveTo(x1, y1);
              ctx.lineTo(x2, y2);
              ctx.stroke();
            }
          }
        }
      }
    }

    // Measure tool: anchors, segments, distance labels (on top of grid)
    const phase = preview?.measurePhase ?? 0;
    const a1 = preview?.measureAnchor1 ?? null;
    const a2 = preview?.measureAnchor2 ?? null;
    const seg2End = preview?.measureSegment2End ?? null;
    const live = preview?.measureLivePoint ?? null;
    /** Draw text right-side up (canvas has scale(1,-1) so text is flipped otherwise). */
    const drawMeasureLabel = (text: string, cx: number, cy: number) => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(1, -1);
      ctx.font = `${Math.max(10, 1200 / scale)}px system-ui, sans-serif`;
      ctx.fillStyle = '#e0e0e0';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 0, 0);
      ctx.restore();
    };

    if (phase >= 1 && a1) {
      const distMm = (p0: Point, p1: Point) =>
        Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
      const toYd = (mm: number) => (mm / YARD_MM).toFixed(1);
      ctx.strokeStyle = '#4a9eff';
      ctx.fillStyle = '#4a9eff';
      ctx.lineWidth = 2 / scale;
      ctx.lineCap = 'round';
      if (phase === 1 && live) {
        ctx.beginPath();
        ctx.moveTo(a1[0], a1[1]);
        ctx.lineTo(live[0], live[1]);
        ctx.stroke();
        const d = distMm(a1, live);
        const midX = (a1[0] + live[0]) / 2;
        const midY = (a1[1] + live[1]) / 2;
        drawMeasureLabel(`${toYd(d)} yd`, midX, midY - 800);
      } else if (phase >= 2 && a2) {
        const end2 = phase === 2 && live ? live : seg2End;
        ctx.beginPath();
        ctx.moveTo(a1[0], a1[1]);
        ctx.lineTo(a2[0], a2[1]);
        ctx.stroke();
        const d1 = distMm(a1, a2);
        drawMeasureLabel(
          `${toYd(d1)} yd`,
          (a1[0] + a2[0]) / 2,
          (a1[1] + a2[1]) / 2 - 800
        );
        if (end2) {
          ctx.beginPath();
          ctx.moveTo(a2[0], a2[1]);
          ctx.lineTo(end2[0], end2[1]);
          ctx.stroke();
          const d2 = distMm(a2, end2);
          drawMeasureLabel(
            `${toYd(d2)} yd`,
            (a2[0] + end2[0]) / 2,
            (a2[1] + end2[1]) / 2 - 800
          );
        }
      }
      ctx.fillStyle = '#4a9eff';
      ctx.beginPath();
      ctx.arc(a1[0], a1[1], MEASURE_ANCHOR_RADIUS_MM, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1 / scale;
      ctx.stroke();
      if (a2) {
        ctx.beginPath();
        ctx.arc(a2[0], a2[1], MEASURE_ANCHOR_RADIUS_MM, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      if (phase === 3 && seg2End) {
        ctx.beginPath();
        ctx.arc(seg2End[0], seg2End[1], MEASURE_ANCHOR_RADIUS_MM, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    ctx.restore();

    // Axis labels just outside the drawing area (only when grid is shown)
    if (getShowGrid === undefined || getShowGrid()) {
      const toScreenX = (mx: number) => originX + mx * scale;
      const toScreenY = (my: number) => originY - (my - hMm / 2) * scale;

      ctx.fillStyle = '#999';
      ctx.font = '11px system-ui, sans-serif';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'right';

      const labelOffsetPx = 18;
      const halfWYd = wMm / YARD_MM / 2;
      const heightYd = hMm / YARD_MM;
      const axisLabelYard = 50; // axis labels every 50 yd (grid stays 25 yd)

      for (let yd = 0; yd <= heightYd + 0.1; yd += axisLabelYard) {
        const my = yd * YARD_MM;
        ctx.fillText(String(yd), toScreenX(-wMm / 2) - labelOffsetPx, toScreenY(my));
      }

      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      for (let xYd = -halfWYd; xYd <= halfWYd + 0.1; xYd += axisLabelYard) {
        const mx = xYd * YARD_MM;
        ctx.fillText(String(xYd), toScreenX(mx), toScreenY(0) + 4);
      }
    }
  }

  function onResize() {
    draw();
  }

  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    view.zoom = Math.max(0.2, Math.min(5, view.zoom * (1 + delta)));
    draw();
  }, { passive: false });

  canvas.addEventListener('mousedown', (e) => {
    if (e.button === 1 || e.button === 2) {
      isPanning = true;
      lastClientX = e.clientX;
      lastClientY = e.clientY;
    }
  });

  canvas.addEventListener('mousemove', (e) => {
    if (isPanning) {
      view.panX += e.clientX - lastClientX;
      view.panY += e.clientY - lastClientY;
      lastClientX = e.clientX;
      lastClientY = e.clientY;
      draw();
    }
  });

  canvas.addEventListener('mouseup', (e) => {
    if (e.button === 1 || e.button === 2) isPanning = false;
  });

  canvas.addEventListener('mouseleave', () => {
    isPanning = false;
  });

  canvas.addEventListener('contextmenu', (e) => e.preventDefault());

  window.addEventListener('resize', onResize);

  return {
    draw,
    screenToMm,
    mmToScreen,
    getScale,
    getView: () => ({ ...view }),
    resetView() {
      view = { ...DEFAULT_VIEW };
      draw();
    },
  };
}

export type CanvasView = ReturnType<typeof createCanvasView>;
